import { ApiError } from "@/lib/api-client";
import { formatDate } from "@/lib/format-date";
import { contractDateMessage } from "@/routes/admin/contract-date-bounds";
import { SEAT_STAYS_CONSUMED_SENTENCE } from "@/routes/org-admin/dashboard-derivations";

// Story 15.6 AC27 — the ONE mapper every refusal on Screens 23 AND 24 reaches the
// operator through. Follows the shipped `create-organization-error.ts` /
// `admin-user-error.ts` convention exactly:
//
//   • branch on `ApiError.code` / `ApiError.status` / a 422 `loc`;
//   • order the CODED branches ahead of the bare statuses, so a specific code
//     always beats a status that several refusals share;
//   • render the server's OWN code verbatim beside the copy;
//   • NEVER branch on the server's English `msg` — prose is not an interface, and
//     a copy edit on the backend must not silently re-route a banner here.
//
// THIS STORY SHIPS FIVE BRANCHES: ORG-016, ORG-018, the 422 `loc` plumbing, the
// shared 401 / uncoded-403, and the default. Two companions extend this same file
// and nothing else:
//
//   • Story 15.2 appends ONE branch — ORG-007 (seat capacity below consumed);
//   • Story 15.3 appends THREE — ORG-001 / ORG-002 / ORG-005 (the contract window
//     rules its extend-contract control can trip).
//
// They append INSIDE the coded block below, above the `error.status === 422`
// branch, and touch nothing else. The file is laid out so that is a pure
// insertion: one message constant beside its neighbours, one `if` beside its
// neighbours. A companion that has to restructure this file to fit its code has
// misread the contract.
//
// Story 15.11 (morning triage, 2026-09-05) appends TWO more the same way —
// ORG-020 / ORG-021, the no-change refusals both write endpoints now raise. The
// mapper entries were carved out of 15.11's own unit so that unit's diff stayed
// clear of `frontend/`, and they are finished here.
//
// Story 21.1 (AC29) appends ONE more coded branch — ORG-022, the renewal's
// below-the-carried-headcount refusal — and one new `surface` value,
// `renew-contract`. Renewal ORG-001 is a fourth raiser of that code; its
// sentence is selected by `details.reason`, and `surface` still only picks the
// field. ORG-002's banner TITLE is the other surface-aware half ("to extend"
// vs "to renew").

/** Banner descriptor. `variant` maps 1:1 onto a `Banner` variant. */
export interface AdminOrgBanner {
  /** `info` = a settled state (no retry); `warn` = check/conflict; `danger` = refusal/transport. */
  variant: "info" | "warn" | "danger";
  title: string;
  body: string;
  /** The server's own error code, rendered verbatim beside the copy (AC27). */
  code?: string;
}

/**
 * Inline messages keyed to the Screen 24 form's field names.
 *
 * The keys are the CLIENT's camelCase field names, not the wire's; the 422
 * plumbing below is the one place that translates between the two, which is why
 * a new editable field costs exactly one `case` there and one key here.
 */
export interface AdminOrgFieldErrors {
  name?: string;
  externalReferenceId?: string;
  billingAddress?: string;
  billingContactEmail?: string;
  securityContactEmail?: string;
  /**
   * The detail form's editable contract date. Filled by a 422 whose `loc` ends in
   * `contract_start_date`, and by ORG-001 on the `update-details` surface — the
   * start date is the only half that form can move.
   */
  contractStartDate?: string;
  /**
   * Story 15.3. The renewal control's one field — ORG-005 attaches here, ORG-001
   * attaches here on the `extend-contract` surface, and so does a 422 whose `loc`
   * ends in `contract_end_date` (AC8): the `le=MAX_CONTRACT_END_DATE` refusal is
   * schema-level and carries NO error code (`exception_handlers.py:179`), so
   * `loc` is the only thing it can be routed by.
   */
  contractEndDate?: string;
  /**
   * Story 15.2. The adjust-seats control's one field — ORG-007 attaches here,
   * and so does ORG-020.
   *
   * Story 21.1 adds a SECOND control that renders a seat-count field: the
   * renewal dialog, where ORG-022 lands on this same key. The two controls
   * never render at once inside one dialog, so one key still serves both.
   */
  seatCapacity?: string;
}

/**
 * WHICH FORM's submit failed — the one thing ORG-001 cannot be routed without.
 *
 * ORG-001 has SIX construction sites across FOUR slices, and they do NOT split
 * "the first three argument-less, then the rest structured". THREE pass no
 * kwargs at all, so `details` is the exception's own window STRING and nothing
 * else (`InvalidContractWindowException.__init__`: `reason is None` → string):
 *
 *   • `create_organization/command.py:217` — both dates submitted, end not
 *     strictly after start. A different screen with its OWN mapper
 *     (`create-organization-error.ts`), so it never reaches this file;
 *   • `extend_contract/command.py:138` — submitted end before the stored start;
 *   • `update_organization/command.py:258` — submitted start after the stored
 *     end.
 *
 * The other THREE pass `reason`, so `details` is an object:
 *
 *   • `extend_contract/command.py:170` — `end_before_current_term`, added by
 *     Franklin#828. The extend control therefore raises BOTH shapes, which is
 *     why "the surface tells you which rule" was never a safe reading and why
 *     the sentence is chosen by `details.reason` in `org001FieldMessage`;
 *   • `renew_contract/command.py:184` — `no_term_to_close`;
 *   • `renew_contract/command.py:207` — `not_strictly_forward`.
 *
 * On an argument-less refusal the wire says the window is invalid and nothing
 * else: it never names the date the operator has to move. Only the surface
 * knows that, because only the surface knows which half of the pair it
 * submitted:
 *
 *   • `extend-contract` submits the END date and nothing else (both of its
 *     guards above refuse an end date);
 *   • `update-details` submits the START date — `contract_end_date` is ABSENT
 *     from that endpoint's request model and a body carrying it is IGNORED
 *     (`update_organization/schemas.py:13-19`, pinned by the shipped
 *     `test_update_details_cannot_move_the_contract_end_date`), so the refusal at
 *     `update_organization/command.py:258` is always about the start date.
 *   • `renew-contract` (Story 21.1) submits the NEW TERM's end date. Its two
 *     ORG-001 facts travel as structured `details.reason` (`no_term_to_close` /
 *     `not_strictly_forward`); `surface` only picks the FIELD, never the
 *     sentence. `ORG-021 contract_end_date_unchanged` is deliberately not
 *     reused for the equal case — the registry pins that code to one raiser,
 *     and "unchanged" cannot describe an EARLIER date, which this rule must
 *     also refuse.
 *
 * The name is the endpoint's own action segment, so the value at a call site and
 * the path that failed read as the same word.
 *
 * DEFAULTS TO `extend-contract`, which is every other caller's shipped behaviour:
 * a READ and the adjust-seats control cannot raise ORG-001 at all. A NEW surface
 * that submits a contract date must pass its own value — a form that leaves the
 * default and has no end-date field renders the refusal nowhere, which is the
 * exact defect this parameter exists to fix.
 */
export type AdminOrgSurface = "extend-contract" | "update-details" | "renew-contract";

export interface MappedAdminOrgError {
  /** Banner descriptor, or null when the failure is fully handled inline. */
  banner: AdminOrgBanner | null;
  /** Per-field messages to attach beside the control the operator must change. */
  fieldErrors: AdminOrgFieldErrors;
}

const GENERIC: AdminOrgBanner = {
  variant: "danger",
  title: "Something went wrong",
  body: "That action could not be completed. Please try again.",
};

// ---------------------------------------------------------------------------
// Coded refusals — one message constant per code, in code order.
// 15.2 adds ORG-007 here; 15.3 adds ORG-001 / ORG-002 / ORG-005 here.
// ---------------------------------------------------------------------------

// ORG-016 — the CRM key already identifies ANOTHER organization. Both halves are
// shown: the banner says what happened and carries the code, the inline message
// points at the one field the operator has to change.
const DUPLICATE_REFERENCE_MESSAGE =
  "That external reference ID already belongs to another organization.";

const DUPLICATE_REFERENCE_BANNER = {
  variant: "warn",
  title: "That external reference ID is already in use",
  body: "Another organization already carries this CRM key. Check the reference in CITS and enter the one for this organization — nothing has been saved.",
} satisfies Omit<AdminOrgBanner, "code">;

// ORG-018 — THIS organization already has a key, and the column is write-once.
// A DIFFERENT fact from ORG-016 with a different remedy, which is exactly why it
// got its own code rather than reusing ORG-016 (boundary row 9): there is nothing
// to retype here, the change simply cannot be made from this console.
const ALREADY_SET_MESSAGE =
  "This organization's external reference ID has already been set and cannot be changed.";

const ALREADY_SET_BANNER = {
  variant: "warn",
  title: "The external reference ID can only be set once",
  body: "This organization already carries a CRM key, so it cannot be replaced from this screen. Nothing has been saved. If the key is wrong, raise it with CITS.",
} satisfies Omit<AdminOrgBanner, "code">;

// ORG-007 (Story 15.2) — the requested capacity is STRICTLY below the seats
// already consumed. The consumed count is read off `details.seat_consumed` as a
// FIELD; the server's English `details.message` carries the same number in prose
// and is never parsed for it. The FR83 rule that causes the refusal is stated by
// importing the sentence that owns it, not by restating it here.
const CAPACITY_BELOW_CONSUMED_BANNER = {
  variant: "warn",
  title: "That capacity is below the seats already in use",
  body: SEAT_STAYS_CONSUMED_SENTENCE,
} satisfies Omit<AdminOrgBanner, "code">;

/**
 * Inline copy for ORG-007. `seat_consumed` is a field, never parsed from prose.
 *
 * Exported because the CLIENT-side below-consumed guard raises the same refusal
 * from the same field before any request is made (Story 15.2 AC7). Two sentences
 * for one rule drift — and they drift into the operator's face, since the stale
 * -LOW case renders the server's copy right after the client's.
 */
export function capacityBelowConsumedMessage(seatConsumed: string | null): string {
  return seatConsumed === null
    ? "Seat capacity cannot be set below the seats already consumed."
    : `Seat capacity cannot be set below the ${seatConsumed} seats already consumed.`;
}

// ORG-001 (Story 15.3) — the contract window is invalid. This is the FALLBACK
// sentence: `org001FieldMessage` reaches it only when `details` carries no
// `reason` it recognises. TWO argument-less guards on these screens land here
// and they move different halves: the extend control submits the END
// (`extend_contract/command.py:129-138`, `end < stored start` refused), and the
// detail form's Start date field submits the START
// (`update_organization/command.py:250-258`, stored `end < submitted start`
// refused). So the sentence names BOTH dates and neither as "the one you just
// changed" — mirroring the server's own `details`, which was reworded for
// exactly this reason.
//
// NOT "whatever the extend control raises". That control has a SECOND ORG-001
// guard (`extend_contract/command.py:170`, Franklin#828) which passes
// `reason="end_before_current_term"` and gets its own sentence below, so the
// discriminator here is the absent reason, never the surface.
//
// ONE SENTENCE, TWO FIELDS: which control the sentence lands ON is the
// `surface` argument's job (see `AdminOrgSurface`), because an argument-less
// refusal cannot tell its raisers apart.
//
// Deliberately NOT 15.1's `INVALID_WINDOW_MESSAGE` ("after the start date"):
// both guards here refuse `end < start` only, so `end == start` succeeds and
// the stricter create-time sentence would misstate the shipped rule.
const INVALID_CONTRACT_WINDOW_MESSAGE =
  "The contract end date must be on or after the contract start date.";

// STORY 21.1 OVERTURNS THE "ONE SENTENCE" HALF ABOVE, and the correction is
// recorded rather than quietly applied: that paragraph used to read "deliberately
// NOT re-worded per surface — the sentence is true wherever it lands". It stopped
// being true when `renew-contract` joined, because ORG-001 there is not the
// end-vs-start window at all.
//
// The split is by RULE (`details.reason`), not by `surface`. `surface` still
// only picks the field. A missing reason keeps the window sentence, and it has
// exactly two live sources: one of the three argument-less construction sites —
// of which `extend_contract/command.py:138` and
// `update_organization/command.py:258` are the two that reach this mapper at
// all — or an envelope predating the `reason` key. NOT "whichever surface
// raised it": `extend-contract` sends both shapes since Franklin#828.
export const RENEWAL_END_DATE_NOT_FORWARD_MESSAGE =
  "The new end date must be later than the current one — a renewal lengthens the contract, it cannot shorten it.";

const NO_TERM_TO_CLOSE_MESSAGE =
  "This organization has no contract term to close, so it cannot be renewed.";

const END_BEFORE_CURRENT_TERM_MESSAGE =
  "The contract end date cannot be before the current term's start.";

function org001FieldMessage(details: unknown): string {
  const reason = readDetailValue(details, "reason");
  if (reason === "no_term_to_close") return NO_TERM_TO_CLOSE_MESSAGE;
  if (reason === "not_strictly_forward") return RENEWAL_END_DATE_NOT_FORWARD_MESSAGE;
  if (reason === "end_before_current_term") return END_BEFORE_CURRENT_TERM_MESSAGE;
  return INVALID_CONTRACT_WINDOW_MESSAGE;
}

// ORG-022 (Story 21.1) — the requested seat count is BELOW the seat holders
// carrying into the new term. The measured count is read off
// `details.active_seat_count` as a FIELD, exactly as ORG-007 reads
// `seat_consumed`; the server's English `details.message` carries the same
// number in prose and is never parsed for it.
//
// THE COUNT IS THE ONE THE SERVER MEASURED UNDER THE ROW LOCK, not the one the
// dialog displayed when it opened. Those can differ — a seat removed between the
// two reads lowers the real floor — and the refusal has to name the number that
// actually caused it, or the operator retries the value the screen told them was
// fine.
//
// THE REMEDY IS HERE AS OF STORY 21.3 (AC15, AC16) — the sentences 21.1's
// boundary row 13 reserved this constant for. Two things the operator needs
// and one they must not be offered:
//
//   * how many seats have to come off — `details.seats_to_release`, read as a
//     FIELD and never parsed out of `details.message`;
//   * WHO can take them off, which is the organization's own administrator and
//     not CITS. The PM ruled on 2026-09-14 that the roster stays org_admin-only
//     and that the super_admin gains no seat-release action — rejected, not
//     deferred — so a banner hinting at a CITS-side release would describe a
//     capability that does not exist and never will;
//   * the second remedy, which is renewing at or above the live headcount, or
//     agreeing a different count with the customer. Without it the banner reads
//     as an instruction to make somebody let a colleague go.
//
// A THIRD SENTENCE WAS CUT BY THE PM (PR #836): "They have already been told
// this on their own dashboard." The banner may not claim the organization was
// notified, because the notice behind that claim is written on a DELIBERATELY
// non-blocking path — `renew_contract/refusal_emit.py` swallows its failures so
// a bookkeeping error can never fail the refusal — and a swallowed write leaves
// the dashboard silent while the banner still asserts delivery. Telling the
// operator they owe no phone call is worth saying only if it is true, and
// nothing on this side knows that it is.
//
// ITS OWN CONSTANT, NOT `SEAT_STAYS_CONSUMED_SENTENCE` (21.1 boundary row 13).
// That constant is Story 21.2's to re-scope to "within the current term", and
// binding a second super_admin message to it would widen 21.2's blast radius.
// It is also simply a different fact: FR83's "a removed seat stays consumed" is
// the rule a renewal RELEASES, so quoting it as the explanation of a renewal's
// refusal would state the opposite of what just happened.
//
// The count-free half stays a plain constant because it is true regardless of
// what `details` carries, and because the null branch below still needs a body.
export const SEATS_BELOW_CARRIED_HEADCOUNT_BANNER_BODY =
  "A new term starts with the people who are already in it, so its seat count cannot be lower than the seats they hold. Nothing has been saved.";

/**
 * The remedy half, which names the figure and the actor.
 *
 * The null branch is not decoration — `details` is a union across this API, and
 * a refusal that arrives without the extended payload (an older build, a proxy
 * that rewrote the envelope) still has to say something true. It drops the
 * number and keeps every other fact, because a sentence carrying the literal
 * "null" is worse than one that omits the figure.
 */
export function seatsBelowCarriedHeadcountRemedy(
  seatsToRelease: string | null,
): string {
  const howMany =
    seatsToRelease === null
      ? "Seats have to be released"
      : seatsToRelease === "1"
        ? "One seat has to be released"
        : `${seatsToRelease} seats have to be released`;
  return (
    `${howMany} by an administrator at the organization — seat release is theirs, ` +
    "not ours. You can also renew at or above their current headcount, or agree " +
    "a different seat count with them."
  );
}

function seatsBelowCarriedHeadcountBanner(
  seatsToRelease: string | null,
): Omit<AdminOrgBanner, "code"> {
  return {
    variant: "warn",
    title: "That seat count is below the seats carrying into the new term",
    body: `${SEATS_BELOW_CARRIED_HEADCOUNT_BANNER_BODY} ${seatsBelowCarriedHeadcountRemedy(seatsToRelease)}`,
  };
}

/**
 * Inline copy for ORG-022 — the measured floor, and nothing beyond it.
 *
 * Exported for the same reason `capacityBelowConsumedMessage` is: the renewal
 * dialog renders the carried headcount read-only, so a reader comparing the two
 * must not meet two different phrasings of one number.
 *
 * The null branch is not decoration. `details` is a union across this API and a
 * refusal that arrives with a bare string — an older build, a proxy that
 * rewrote the envelope — still has to say something true, and a message
 * carrying the literal "null" is worse than one that omits the figure.
 */
export function seatsBelowCarriedHeadcountMessage(activeSeatCount: string | null): string {
  return activeSeatCount === null
    ? "The new term's seat count cannot be below the seats carrying into it."
    : `The new term's seat count cannot be below the ${activeSeatCount} seats carrying into it.`;
}

// ORG-034 (Story 21.5, FR87) — the renewal was recorded too far ahead of the
// stored end date. Renewal opens 30 days before that date, because a renewal
// re-establishes the seat pool on the day it is recorded: accepting one months
// early would release the organization's seats months before the term it paid
// for had ended.
//
// The count-free half stays a plain constant for the ORG-022 reason: it is true
// whatever `details` carries, and the null branch below still needs a body.
//
// MIRRORED from `src/application/organizations/_shared/renewal_window.py ::
// REMINDER_WINDOW_DAYS`. The sentence states the NUMBER; it never computes the
// DATE — that is read off the server payload below, which is what keeps the
// reminder email and the API from drifting. But the number itself is a second
// copy, so moving the constant means editing this sentence too: nothing goes
// red if you forget, and the banner would then state a horizon the API does not
// enforce. The marker is here so a grep for the constant finds this line.
export const RENEWAL_TOO_EARLY_BANNER_BODY =
  "Renewing re-establishes the seat pool on the day it is recorded, so it opens 30 days before the contract ends. Nothing has been saved.";

/**
 * The half that names the date, and the only place it comes from.
 *
 * `earliestRenewalDate` is read off the SERVER's payload — this module never
 * subtracts 30 from the stored end date to compute it, because two independent
 * DERIVATIONS are how the renewal-reminder email and the API start disagreeing
 * for organizations sitting between them. One definition of the horizon lives
 * in `organizations/_shared/renewal_window.py`. (The banner body above does
 * restate the number in prose, under a MIRRORED marker — a second statement of
 * the figure, never a second computation of the date.)
 *
 * The null branch is not decoration: `details` is a union across this API, and
 * a refusal arriving without the extended payload (an older build, a proxy that
 * rewrote the envelope) still has to say something true. It drops the date and
 * keeps every other fact, because a sentence carrying the literal "null" is
 * worse than one that omits the figure.
 */
export function renewalTooEarlyRemedy(earliestRenewalDate: string | null): string {
  return earliestRenewalDate === null
    ? "Come back closer to the end of the current term, or extend the contract instead."
    : `This contract can be renewed from ${formatDate(earliestRenewalDate)} onwards. Until then, extend the contract instead if the dates need to move.`;
}

function renewalTooEarlyBanner(
  earliestRenewalDate: string | null,
): Omit<AdminOrgBanner, "code"> {
  return {
    variant: "warn",
    title: "This contract cannot be renewed yet",
    body: `${RENEWAL_TOO_EARLY_BANNER_BODY} ${renewalTooEarlyRemedy(earliestRenewalDate)}`,
  };
}

/**
 * Inline copy for ORG-034, on the end-date field.
 *
 * The field is the end-date input and not the seat count, and that is the
 * refusal's own shape: nothing is wrong with either number the operator typed.
 * What is wrong is WHEN the renewal is being recorded, which is the fact the
 * end-date field carries on this dialog.
 */
export function renewalTooEarlyMessage(earliestRenewalDate: string | null): string {
  return earliestRenewalDate === null
    ? "This contract cannot be renewed yet."
    : `This contract cannot be renewed until ${formatDate(earliestRenewalDate)}.`;
}

// ORG-005 (Story 15.3) — the requested end date is in the past (UTC). Today is
// accepted (`ContractEndDateInPastException`: "end == today is allowed"), which
// the copy has to say, because "not in the past" reads as excluding today.
//
// ONE SENTENCE ACROSS ALL THREE SURFACES, including 21.1's `renew-contract`
// (AC29), because unlike ORG-001 the RULE does not change: the date must not
// precede today, on whichever form submitted it, and the sentence names exactly
// that. Renewal is where it is most reachable rather than where it means
// something else — a LAPSED organization's stored end date is already in the
// past, so a date that clears the strictly-forward rule can still land behind
// today, and this is the refusal that catches it.
const CONTRACT_END_DATE_IN_PAST_MESSAGE =
  "The end date cannot be in the past. Today's date is accepted.";

// ORG-002 (Story 15.3) — the TARGET organization is a personal one, so it has no
// B2B contract to extend or renew. The server's `details` string is honest for
// both raisers (dashboard actor-claim AND a super_admin targeting a personal
// org); this file still writes operator-facing copy. The TITLE is
// surface-aware; the body can stay.
const NOT_PROFESSIONAL_ORG_BANNER = {
  variant: "warn",
  title: "That organization has no contract to extend",
  body: "This is a personal organization — it carries one owner seat and no B2B contract, so there is no end date to renew. Nothing has been saved.",
} satisfies Omit<AdminOrgBanner, "code">;

// ORG-020 / ORG-021 (Story 15.11) — the submitted value is the one already
// stored, so the write would assert a change that did not happen. Both are
// PERMANENT refusals: the generic default branch ("Please try again") is
// actively false for them, which is the whole reason these two branches exist.
//
// Both are inline-only, following ORG-005 rather than ORG-007: exactly one
// field is wrong, the operator is looking at it, and the remedy is to type a
// different value or cancel. A banner as well would say it twice.

/**
 * Inline copy for ORG-020.
 *
 * Lives HERE, not on the control, because two callers raise the same refusal
 * from the same field: `AdjustSeatCapacityControl.validate()` refuses the
 * no-change value client-side, and the server refuses it again when the card's
 * cached `seatCapacity` is stale (the prop is the TanStack cache, and
 * `seat_capacity` moves in BOTH directions — unlike `seat_consumed`). Same
 * layering as `capacityBelowConsumedMessage` above, and the reason is the same:
 * two sentences for one rule drift, and they drift into the operator's face.
 *
 * It is a verbatim match against the server's `ORG-020` sentence
 * (`organizations/_shared/exceptions.py`), which 15.11 boundary row 8 pinned to
 * this constant's wording.
 */
export const SEAT_CAPACITY_UNCHANGED_MESSAGE =
  "That is the current seat capacity — change the number or cancel.";

/**
 * Inline copy for ORG-021 — the server's own sentence, verbatim.
 *
 * NOT exported, unlike its ORG-020 sibling: `ExtendContractControl` has no
 * client-side no-change guard to share it with, so a second consumer would be
 * inventing one. That asymmetry is also why ORG-021 is the more exposed of the
 * two — the control PRE-FILLS the dialog with the stored date, so opening it
 * and pressing save without editing reaches this refusal with no stale cache
 * required.
 */
const CONTRACT_END_DATE_UNCHANGED_MESSAGE =
  "That is the current contract end date — change the date or cancel.";

// An un-coded 404 — the house not-found envelope (`organization_not_found`); no
// ORG-* code is allocated for it, so it is matched on the STATUS and only after
// every coded branch has had its turn.
const NOT_FOUND: AdminOrgBanner = {
  variant: "warn",
  title: "That organization could not be found",
  body: "It may have been removed, or the link may be wrong. Return to the organization directory and search for it again.",
};

// 401 and an UNCODED 403 are one user-visible situation — the admin surfaces
// answer an anonymous caller with 403 rather than 401, so keying "signed out" off
// 401 alone would report an expired session as a permanent capability refusal.
const SESSION_EXPIRED: AdminOrgBanner = {
  variant: "warn",
  title: "Your session may have expired",
  body: "Sign in again and retry this action.",
};

/**
 * True when the SERVER named the failure. `UNKNOWN` is `api-client`'s placeholder
 * for a response that carried no code at all, so it is not a code.
 */
function hasServerCode(error: ApiError): boolean {
  return Boolean(error.code) && error.code !== "UNKNOWN";
}

/**
 * Read one named field out of a STRUCTURED `details` object.
 *
 * Copied from `admin-user-error.ts:264-281`. `details` is a union across this
 * API — a bare string on ORG-002 / ORG-005 and on ORG-001 when raised without
 * kwargs, a structured object on renewal / #828 ORG-001, a 422 detail ARRAY,
 * and an object on ORG-007 — so the array and string shapes have to fall
 * through to `null` rather than throw. Numbers are stringified here so the
 * caller never has to know which of the two the server sent.
 */
function readDetailValue(details: unknown, key: string): string | null {
  if (details && typeof details === "object" && !Array.isArray(details) && key in details) {
    const value = (details as Record<string, unknown>)[key];
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
  }
  return null;
}

/**
 * Translate one 422 detail item into our own field message.
 *
 * The field is read off the TAIL of `loc`, not off `loc[1]`: FastAPI's location
 * tuple is `("body", "<field>")` for a flat model but grows a prefix the moment
 * anything nests, and a fixed index silently stops matching when it does. Copied
 * verbatim from `create-organization-error.ts:95-144`.
 */
function applyDetailItem(item: unknown, into: AdminOrgFieldErrors): void {
  if (!item || typeof item !== "object") return;
  const loc = (item as { loc?: unknown }).loc;
  const field = Array.isArray(loc) && loc.length > 0 ? String(loc[loc.length - 1]) : "";

  switch (field) {
    case "name":
      into.name ??= "Enter the legal organization name.";
      break;
    case "external_reference_id":
      into.externalReferenceId ??= "Enter the external reference ID supplied by CITS.";
      break;
    case "billing_address":
      into.billingAddress ??= "Enter the billing address.";
      break;
    case "billing_contact_email":
      into.billingContactEmail ??= "Enter a valid email address.";
      break;
    case "security_contact_email":
      into.securityContactEmail ??= "Enter a valid email address.";
      break;
    // Story 15.3 AC8 (end) and the 15.6 review round (start). Both edges carry
    // `le=MAX_CONTRACT_END_DATE`, and that refusal is schema-level with no error
    // code, so `loc` is the only thing routing it to a FIELD.
    //
    // WHICH refusal it is, though, is `type`'s job and not `loc`'s — see
    // `contract-date-bounds.ts`. The ceiling sentence has to name the bound,
    // because nothing on the screen does; naming it at a parse failure or an
    // explicit null is the same mistake in the other direction.
    case "contract_start_date":
      into.contractStartDate ??= contractDateMessage("start", item);
      break;
    case "contract_end_date":
      into.contractEndDate ??= contractDateMessage("end", item);
      break;
    // NO `seat_capacity` CASE, on both seat-writing surfaces and deliberately:
    // `ge=1` and the int32 ceiling are the only schema refusals that column
    // has, and BOTH controls that submit it guard those bounds client-side
    // before any request is made. A case here would route a 422 neither dialog
    // can produce.
  }
}

function parse422Details(details: unknown): AdminOrgFieldErrors {
  const fieldErrors: AdminOrgFieldErrors = {};
  if (Array.isArray(details)) {
    for (const item of details) applyDetailItem(item, fieldErrors);
  }
  return fieldErrors;
}

/**
 * Map any organization directory / detail failure to inline errors + a banner.
 *
 * `surface` names the form that submitted; it changes ONE branch (ORG-001) and
 * nothing else, and defaults to the shipped `extend-contract` routing.
 */
export function toAdminOrgError(
  error: unknown,
  surface: AdminOrgSurface = "extend-contract",
): MappedAdminOrgError {
  if (error instanceof ApiError) {
    // --- Coded branches first: a specific code always beats a bare status. ---
    if (error.code === "ORG-016") {
      return {
        fieldErrors: { externalReferenceId: DUPLICATE_REFERENCE_MESSAGE },
        // The code comes off the RESPONSE, not a literal. The branch is keyed on
        // ORG-016 so the two agree today, but reading it back is what keeps the
        // rendered support handle honest through the next re-allocation — the
        // create path already had one code re-numbered under it.
        banner: { ...DUPLICATE_REFERENCE_BANNER, code: error.code },
      };
    }
    if (error.code === "ORG-018") {
      return {
        fieldErrors: { externalReferenceId: ALREADY_SET_MESSAGE },
        banner: { ...ALREADY_SET_BANNER, code: error.code },
      };
    }

    // --- Story 15.2 ---
    if (error.code === "ORG-007") {
      return {
        fieldErrors: {
          seatCapacity: capacityBelowConsumedMessage(
            readDetailValue(error.details, "seat_consumed"),
          ),
        },
        banner: { ...CAPACITY_BELOW_CONSUMED_BANNER, code: error.code },
      };
    }

    // --- Story 15.3 ---
    if (error.code === "ORG-001") {
      // `surface` picks the FIELD. `details.reason` picks the SENTENCE. A
      // renewal ORG-001 without a reason (older envelope) falls through to the
      // window sentence rather than guessing the strictly-forward rule.
      const sentence = org001FieldMessage(error.details);
      if (surface === "update-details") {
        return {
          fieldErrors: { contractStartDate: sentence },
          banner: null,
        };
      }
      return {
        fieldErrors: { contractEndDate: sentence },
        // No banner: exactly one field is wrong and the operator is looking at
        // it. A banner as well would say the same thing twice (AC3).
        banner: null,
      };
    }
    if (error.code === "ORG-005") {
      return {
        fieldErrors: { contractEndDate: CONTRACT_END_DATE_IN_PAST_MESSAGE },
        banner: null,
      };
    }
    if (error.code === "ORG-002") {
      // Banner, not inline: no date the operator can type fixes a personal
      // organization, so pointing at the field would be the wrong remedy (AC6).
      // Title is the one surface-aware half — extend vs renew.
      return {
        fieldErrors: {},
        banner: {
          ...NOT_PROFESSIONAL_ORG_BANNER,
          title:
            surface === "renew-contract"
              ? "That organization has no contract to renew"
              : NOT_PROFESSIONAL_ORG_BANNER.title,
          code: error.code,
        },
      };
    }

    // --- Story 15.11 ---
    if (error.code === "ORG-020") {
      return {
        fieldErrors: { seatCapacity: SEAT_CAPACITY_UNCHANGED_MESSAGE },
        banner: null,
      };
    }
    if (error.code === "ORG-021") {
      // NOT surface-routed, unlike ORG-001 above, and that rests on an
      // invariant worth naming: `extend-contract` is the ONLY raiser, because
      // `update_organization`'s request model excludes `contract_end_date`
      // outright and a body carrying it is ignored (pinned by
      // `test_update_details_cannot_move_the_contract_end_date`). If that
      // exclusion is ever lifted, this branch needs `surface` the way ORG-001
      // does — `update-details` renders the end date as read-only text, so a
      // message attached to it there is one no operator can act on.
      return {
        fieldErrors: { contractEndDate: CONTRACT_END_DATE_UNCHANGED_MESSAGE },
        banner: null,
      };
    }

    // --- Story 21.1, remedy added by 21.3 ---
    if (error.code === "ORG-022") {
      return {
        fieldErrors: {
          seatCapacity: seatsBelowCarriedHeadcountMessage(
            readDetailValue(error.details, "active_seat_count"),
          ),
        },
        // BOTH halves, following ORG-007 rather than ORG-005: the inline
        // message is what the operator retypes against, and the banner is what
        // explains a refusal they have not met before on a control that has
        // just told them a lower number is allowed. An inline-only refusal here
        // reads as a validation typo.
        //
        // The payload is a CLOSED set as of Story 21.3, not a base: `details`
        // is exactly `active_seat_count`, `requested_seat_capacity`,
        // `seats_to_release` and `message`. Pinned server-side by
        // `test_org_022_details_carries_three_counts_and_no_person`
        // (`renew_contract/tests/integration/test_renew_contract_endpoint.py`),
        // which asserts the key set with `==` — so a fifth key FAILS it, and
        // widening the payload is a deliberate edit to a tested set.
        // `CapacityBelowActiveSeatsException`'s docstring and the ORG-* registry
        // both say the same.
        //
        // Neither key is nevertheless pinned HERE, and closing the set upstream
        // did not change that: `readDetailValue` answers `null` for anything
        // that is not a structured object carrying the key — an older build, a
        // proxy that rewrote the envelope — and both message helpers have a
        // null branch that drops the figure and keeps every other fact. The
        // closed set is the server's contract; degrading rather than failing on
        // an envelope that does not honour it is this branch's.
        banner: {
          ...seatsBelowCarriedHeadcountBanner(
            readDetailValue(error.details, "seats_to_release"),
          ),
          code: error.code,
        },
      };
    }

    // --- Story 21.5 (FR87), PM ruling 2026-09-16 on Franklin#829 ---
    if (error.code === "ORG-034") {
      return {
        fieldErrors: {
          // The END DATE field, not the seat count. Both numbers the operator
          // typed are fine; what is wrong is when the renewal is being
          // recorded, and the end date is where that fact lives on this dialog.
          contractEndDate: renewalTooEarlyMessage(
            readDetailValue(error.details, "earliest_renewal_date"),
          ),
        },
        // BOTH halves, on the ORG-022 precedent: this is a refusal the
        // operator has not met before, on a control that gives no hint the
        // action has a season. An inline-only message reads as a date typo —
        // which is the one thing it is not.
        banner: {
          ...renewalTooEarlyBanner(
            readDetailValue(error.details, "earliest_renewal_date"),
          ),
          code: error.code,
        },
      };
    }

    if (error.status === 422) {
      const fieldErrors = parse422Details(error.details);
      const handledInline = Object.keys(fieldErrors).length > 0;
      return {
        fieldErrors,
        // Only banner when nothing could be attached to a field, so the operator
        // is never told "check the details" with no field actually flagged.
        banner: handledInline
          ? null
          : {
              variant: "warn",
              title: "Check the details",
              body: "Please review the fields and try again.",
            },
      };
    }

    // The house un-coded 404. A CODED 404 (none exists today) falls through to
    // the default branch, which names the server's own code rather than guessing.
    if (error.status === 404 && !hasServerCode(error)) {
      return { fieldErrors: {}, banner: NOT_FOUND };
    }

    // Only an UNCODED 403 joins 401 here, matching `admin-user-error.ts`. The
    // capability refusal these endpoints raise is uncoded —
    // `requires_capability` raises `InsufficientPermissionsException`, which
    // ships with no `error_code`, so `api-client` fills the literal `UNKNOWN` —
    // and it is indistinguishable on the wire from the anonymous caller. "Sign in
    // again" is the honest remedy for both, and the route guard already keeps a
    // non-super_admin off these screens.
    if (error.status === 401 || (error.status === 403 && !hasServerCode(error))) {
      return { fieldErrors: {}, banner: SESSION_EXPIRED };
    }

    // Default — surface the server's OWN code verbatim. No translation table, so
    // a code this file does not own cannot be mis-explained. Status 0 is a
    // transport failure whose "code" is the client's own placeholder, and quoting
    // it would send an operator to support with a string the server never sent.
    if (error.status !== 0 && hasServerCode(error)) {
      return { fieldErrors: {}, banner: { ...GENERIC, code: error.code } };
    }
  }
  // NETWORK_ERROR / AuthExpiredError / unknown — nothing internal leaked.
  return { fieldErrors: {}, banner: GENERIC };
}
